import { useState } from "react";
import { ArrowRight, Phone, MessageCircle, Loader2, CheckCircle2, ShieldCheck, Award, Clock, Heart } from "lucide-react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { SERVICES } from "../data/services";
import { SITE, callLink, whatsappLink, ASSETS } from "../lib/constants";
import useReveal from "../hooks/useReveal";

const TRUST_ITEMS = [
  { icon: "ShieldCheck", title: "Sterilized & Safe", desc: "Hospital-grade hygiene." },
  { icon: "Award", title: "MDS Specialists", desc: "Top-rated experienced doctors." },
  { icon: "Clock", title: "Same-Day Care", desc: "Walk-ins welcome daily." },
  { icon: "Heart", title: "Painless Treatment", desc: "Modern, gentle dentistry." },
];

export default function BookAppointment() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid name and 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      setSuccess(true);
      toast.success("Appointment requested!", {
        description: "Our team will call you within 30 minutes.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      setForm({
        name: "",
        phone: "",
        email: "",
        service: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Book Dental Appointment in Allahabad | Expert Dentist Consultation"
        description="Book your dental appointment at Lotus Family Dental in Allahabad. Same-day painless treatment, MDS specialists, transparent pricing. Call +91 9407018052."
        keywords="book dental appointment allahabad, dentist consultation prayagraj"
        canonical="https://lotusfamilydental.me/book-appointment"
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-12 lg:pt-16 pb-12">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-teal-100/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
                Same-day appointments
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 leading-[1.05]">
                Book Your Dental Appointment in <span className="text-teal-700 font-medium">Allahabad Today</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Same-day painless treatment available. Fill the short form below — our team will call you
                within 30 minutes to confirm.
              </p>

              {success && (
                <div className="mt-6 inline-flex items-start gap-3 bg-teal-50 border border-teal-100 rounded-2xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-teal-900">Appointment received!</div>
                    <div className="text-sm text-teal-800 mt-0.5">We'll call you within 30 minutes.</div>
                  </div>
                </div>
              )}

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {TRUST_ITEMS.map((t, i) => {
                  const Icon = Icons[t.icon] || Icons.ShieldCheck;
                  return (
                    <div
                      key={i}
                      className="reveal bg-white rounded-2xl p-4 border border-slate-100"
                      style={{ transitionDelay: `${i * 50}ms` }}
                    >
                      <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="mt-3 font-medium text-sm text-slate-900">{t.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form card */}
            <div className="lg:col-span-5">
              <div className="reveal bg-white rounded-3xl border border-slate-100 shadow-2xl shadow-slate-200/50 p-7 lg:p-8">
                <h2 className="font-heading text-2xl font-medium text-slate-900">Quick Appointment</h2>
                <p className="text-sm text-slate-500 mt-1">Takes less than 30 seconds.</p>

                <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Full Name *</Label>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                      data-testid="book-form-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Phone *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 e.g. 9407018052"
                      className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                      data-testid="book-form-phone"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Preferred Date</Label>
                      <Input
                        name="preferred_date"
                        type="date"
                        value={form.preferred_date}
                        onChange={handleChange}
                        className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                        data-testid="book-form-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Preferred Time</Label>
                      <select
                        name="preferred_time"
                        value={form.preferred_time}
                        onChange={handleChange}
                        className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                        data-testid="book-form-time"
                      >
                        <option value="">Any time</option>
                        <option>10:00 AM – 12:00 PM</option>
                        <option>12:00 PM – 02:00 PM</option>
                        <option>02:00 PM – 04:00 PM</option>
                        <option>04:00 PM – 06:00 PM</option>
                        <option>06:00 PM – 09:00 PM</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Treatment</Label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                      data-testid="book-form-service"
                    >
                      <option value="">Select treatment (optional)</option>
                      <option value="General Consultation">General Consultation</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Message</Label>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Anything else? (optional)"
                      className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-teal-500 resize-none"
                      data-testid="book-form-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="book-form-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Confirm Appointment <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <a
                      href={callLink}
                      className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Phone className="w-4 h-4" /> Call
                    </a>
                    <a
                      href={whatsappLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1faa55]"
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Services */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Popular treatments
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              What can we help you with today?
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="reveal bg-white rounded-3xl p-6 border border-slate-100 premium-card group flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.tagline}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emergency */}
      <section className="py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 p-8 lg:p-10">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-2xl lg:text-3xl font-medium text-slate-900">
                  Need Emergency Dental Care?
                </h2>
                <p className="mt-2 text-slate-600">
                  Tooth pain, broken tooth, or sudden swelling? We provide same-day emergency appointments.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={callLink}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800"
                  data-testid="emergency-call-button"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
                <a
                  href={whatsappLink("URGENT: I need emergency dental care.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1faa55]"
                  data-testid="emergency-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
